import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useContractorAuth } from "@/hooks/useContractorAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Copy, Trash2, ChevronRight } from "lucide-react";
import { WOOD_SPECIES, FINISHES, DOOR_STYLES, CONSTRUCTION_TYPES } from "@/data/cabinetDefaults";

interface Project {
  id: string;
  name: string;
  client_name: string;
  status: string;
  wood_species: string;
  finish: string;
  door_style: string;
  created_at: string;
  updated_at: string;
  cabinet_count?: number;
}

interface Props {
  /** Where to navigate when opening a project. Defaults to contractor route. */
  projectRoute?: (id: string) => string;
}

const ContractorProjectsView = ({
  projectRoute = (id) => `/contractor/projects/${id}`,
}: Props) => {
  const { profile, loading } = useContractorAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [newName, setNewName] = useState("");
  const [newClient, setNewClient] = useState("");
  const [newWood, setNewWood] = useState("");
  const [newFinish, setNewFinish] = useState("");
  const [newDoorStyle, setNewDoorStyle] = useState("");
  const [newConstructionType, setNewConstructionType] = useState("Overlay");

  useEffect(() => {
    if (profile) fetchProjects();
  }, [profile]);

  const fetchProjects = async () => {
    if (!profile) return;
    setLoadingProjects(true);
    const { data } = await supabase
      .from("projects")
      .select("*")
      .eq("contractor_id", profile.id)
      .order("updated_at", { ascending: false });

    if (data) {
      const projectIds = data.map((p: any) => p.id);
      const { data: cabinets } = await supabase
        .from("project_cabinets")
        .select("project_id, quantity")
        .in("project_id", projectIds);

      const countMap: Record<string, number> = {};
      cabinets?.forEach((c: any) => {
        countMap[c.project_id] = (countMap[c.project_id] || 0) + (c.quantity || 1);
      });

      setProjects(
        data.map((p: any) => ({
          ...p,
          cabinet_count: countMap[p.id] || 0,
        }))
      );
    }
    setLoadingProjects(false);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || !newName.trim()) return;

    const { data: createdProject, error } = await supabase.from("projects").insert({
      contractor_id: profile.id,
      name: newName.trim(),
      client_name: newClient.trim(),
      wood_species: newWood,
      finish: newFinish,
      door_style: newDoorStyle,
      construction_type: newConstructionType.toLowerCase(),
    } as any).select("id").single();

    if (error) {
      toast.error("Failed to create project");
      console.error(error);
    } else if (createdProject) {
      toast.success("Project created");
      setDialogOpen(false);
      setNewName("");
      setNewClient("");
      setNewWood("");
      setNewFinish("");
      setNewDoorStyle("");
      setNewConstructionType("Overlay");
      navigate(projectRoute(createdProject.id));
    }
  };

  const handleDuplicate = async (project: Project) => {
    if (!profile) return;
    const { data: newProject, error } = await supabase
      .from("projects")
      .insert({
        contractor_id: profile.id,
        name: `${project.name} (Copy)`,
        client_name: project.client_name,
        wood_species: project.wood_species,
        finish: project.finish,
        door_style: project.door_style,
      })
      .select()
      .single();

    if (error || !newProject) {
      toast.error("Failed to duplicate project");
      return;
    }

    const { data: cabinets } = await supabase
      .from("project_cabinets")
      .select("*")
      .eq("project_id", project.id);

    if (cabinets && cabinets.length > 0) {
      await supabase.from("project_cabinets").insert(
        cabinets.map((c: any) => ({
          project_id: newProject.id,
          cabinet_type: c.cabinet_type,
          width_inches: c.width_inches,
          height_inches: c.height_inches,
          depth_inches: c.depth_inches,
          quantity: c.quantity,
          label: c.label,
          accessories: c.accessories,
          sort_order: c.sort_order,
        }))
      );
    }

    const { data: extras } = await supabase
      .from("project_extras")
      .select("*")
      .eq("project_id", project.id);

    if (extras && extras.length > 0) {
      await supabase.from("project_extras").insert(
        extras.map((ex: any) => ({
          project_id: newProject.id,
          extra_type: ex.extra_type,
          quantity: ex.quantity,
          notes: ex.notes,
          label: ex.label,
          unit_price: ex.unit_price,
        }))
      );
    }

    toast.success("Project duplicated");
    fetchProjects();
  };

  const handleDelete = async (projectId: string) => {
    if (!confirm("Delete this project? This cannot be undone.")) return;
    const { error } = await supabase.from("projects").delete().eq("id", projectId);
    if (error) {
      toast.error("Failed to delete project");
    } else {
      toast.success("Project deleted");
      fetchProjects();
    }
  };

  const statusColors: Record<string, string> = {
    draft: "bg-muted text-muted-foreground",
    submitted: "bg-primary/10 text-primary",
    quoted: "bg-primary/10 text-primary",
  };

  if (loading) {
    return <p className="text-muted-foreground">Loading...</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 sm:mb-8 gap-3">
        <h2
          className="text-2xl sm:text-3xl font-bold text-foreground"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Jobs
        </h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="lg" className="font-bold text-base">
              <Plus className="w-4 h-4" /> Start New Job
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start New Job</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label htmlFor="new-job-name" className="text-sm font-semibold text-foreground">Job Name *</Label>
                <Input id="new-job-name" value={newName} onChange={(e) => setNewName(e.target.value)} required autoFocus className="mt-1" placeholder="e.g. Smith Kitchen Remodel" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Client Name</Label>
                <Input value={newClient} onChange={(e) => setNewClient(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Wood Species</Label>
                <Select value={newWood} onValueChange={setNewWood}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select wood species" /></SelectTrigger>
                  <SelectContent>
                    {WOOD_SPECIES.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Finish</Label>
                <Select value={newFinish} onValueChange={setNewFinish}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select finish" /></SelectTrigger>
                  <SelectContent>
                    {FINISHES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Door Style</Label>
                <Select value={newDoorStyle} onValueChange={setNewDoorStyle}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select door style" /></SelectTrigger>
                  <SelectContent>
                    {DOOR_STYLES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Construction Type</Label>
                <Select value={newConstructionType} onValueChange={setNewConstructionType}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select construction type" /></SelectTrigger>
                  <SelectContent>
                    {CONSTRUCTION_TYPES.map((ct) => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full font-bold" size="lg">Create and Open Job</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loadingProjects ? (
        <p className="text-muted-foreground">Loading projects...</p>
      ) : projects.length === 0 ? (
        <div className="text-center py-20">
           <p className="text-foreground mb-4">No jobs yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map((project) => (
            <div
              key={project.id}
              className="border-2 border-border p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2 sm:justify-between hover:border-primary transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <h3 className="font-bold text-lg text-foreground break-words">{project.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full uppercase tracking-wider ${statusColors[project.status] || ""}`}>
                    {project.status}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1 break-words">
                  {project.client_name && `${project.client_name} · `}
                  {project.cabinet_count} cabinet{project.cabinet_count !== 1 ? "s" : ""}
                  {project.wood_species && ` · ${project.wood_species}`}
                  {project.door_style && ` · ${project.door_style}`}
                </p>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 self-end sm:self-auto">
                <Button size="sm" className="font-semibold" onClick={() => navigate(projectRoute(project.id))}>
                  Open Job <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDuplicate(project)} title="Duplicate">
                  <Copy className="w-4 h-4" />
                </Button>
                {project.status === "draft" && (
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(project.id)} title="Delete">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractorProjectsView;
